import { Reducer } from 'redux'

import { SliceComparator, SliceWithoutRange, TextOperation, ISlice } from '../constants'
import { concatRanges, createFragment, createRange, subtractRanges } from '../range'

import { sliceReducer } from './slice'

const initialState: ReadonlyArray<ISlice> = []

export const createSlicesReducer = (
  slicesEqual: SliceComparator = defaultSlicesEqual,
): Reducer<typeof initialState, TextOperation> => {
  return (state = initialState, operation) => {
    const slices = state.map((slice) => sliceReducer(slice, operation))
    if (slices.some((slice, index) => slice !== state[index])) {
      state = slices
    }

    if (operation.type === 'insert') {
      state = subtractSlice(
        state,
        createFragment(operation.start, operation.text.length),
        () => true,
      )
      for (const s of operation.slices ?? []) {
        const adjustedRange = createRange(operation.start + s.range.start, s.range.length)
        state = mergeSlice(state, { ...s, range: adjustedRange }, slicesEqual)
      }
    }
    if (operation.type === 'delete') {
      state = mergeEqualSlices(state, slicesEqual)
    }
    if (operation.type === 'slice-insert') {
      state = mergeSlice(state, operation.slice, slicesEqual)
    }
    if (operation.type === 'slice-delete') {
      state = subtractSlice(state, operation.slice, slicesEqual)
    }

    const withoutDeleted = state.filter((slice) => slice.range.length > 0)
    if (withoutDeleted.length !== slices.length) {
      state = withoutDeleted
    }

    return state
  }
}

export const slicesReducer = createSlicesReducer()

function defaultSlicesEqual(a: SliceWithoutRange, b: SliceWithoutRange): boolean {
  return a === b
}

function subtractSlice(slices: ReadonlyArray<ISlice>, slice: ISlice, slicesEqual: SliceComparator) {
  const result = []
  for (const s of slices) {
    if (!slicesEqual(s, slice)) {
      result.push(s)
      continue
    }

    const [range1, range2] = subtractRanges(s.range, slice.range)
    result.push({ ...s, range: range1 })
    if (range2) {
      result.push({ ...s, range: range2 })
    }
  }
  return result
}

function mergeSlice(slices: ReadonlyArray<ISlice>, slice: ISlice, slicesEqual: SliceComparator) {
  let index = 0
  let insertAt = -1
  const result = []
  for (const s of slices) {
    const range = slicesEqual(s, slice) ? concatRanges(s.range, slice.range) : undefined
    if (range) {
      slice = { ...s, range }
      insertAt = index
      continue
    }
    result.push(s)
    index++
  }
  if (insertAt === -1) {
    result.push(slice)
  } else {
    result.splice(insertAt, 0, slice)
  }
  return result
}

function mergeEqualSlices(slices: ReadonlyArray<ISlice>, slicesEqual: SliceComparator) {
  let result: ISlice[] = []
  for (const s of slices) {
    result = mergeSlice(result, s, slicesEqual)
  }
  if (result.length !== slices.length) {
    return result
  }
  return slices
}
